import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, ExternalLink } from "lucide-react";
import { useActivityEvents } from "../../hooks/useActivityEvents";
import apiService from "../../services/api";

export default function ActivitiesSection() {
  const [visibleCount, setVisibleCount] = useState(4);
  const { activities, loading } = useActivityEvents();

  const handleLoadMore = () => {
    setVisibleCount((prev) => prev + 4);
  };

  if (loading) {
    return (
      <section className="mt-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-14">
              <h2 className="text-3xl md:text-5xl font-bold text-gray-900 tracking-tight my-5">
                Recent Activities
              </h2>
              <p className="text-lg text-gray-600 tracking-wide mb-5">
                Stay updated with our latest events and meetings
              </p>
            </div>
            <div className="flex justify-center items-center py-12">
              <p className="text-gray-500">Loading activities...</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (activities.length === 0) {
    return (
      <section className="mt-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Recent Activities
            </h2>
            <p className="text-xl text-gray-600">
              No activities available at the moment.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="mt-20 bg-white">
      <div className="container mx-auto px-6">
        <div className="max-w-7xl mx-auto px-6">
          {/* Header */}
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 tracking-tight my-5">
              Recent Activities
            </h2>
            <p className="text-lg text-gray-600 tracking-wide mb-5">
              Stay updated with our latest events and meetings
            </p>
          </div>

          {/* Activities Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {activities.slice(0, visibleCount).map((activity) => (
              <Link
                key={activity.id}
                to={activity.link}
                className="group bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1.5"
              >
                {/* Poster */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={apiService.getImageById(activity.public_id)}
                    alt={activity.title}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent pointer-events-none" />
                  {activity.date && (
                    <div className="absolute bottom-3 left-3 flex items-center text-white text-sm space-x-2">
                      <Calendar size={16} />
                      <span>&nbsp;{activity.date}</span>
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-3 space-y-4">
                  <h3 className="font-semibold text-gray-900 text-lg leading-relaxed transition-colors group-hover:text-blue-600 mb-3">
                    {activity.title}
                  </h3>
                  <div className="flex items-center text-blue-600 text-sm font-medium tracking-wide">
                    Read More <ExternalLink size={14} className="ml-1" />
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Load More Button */}
          {visibleCount < activities.length && (
            <div className="mt-12 flex justify-center">
              <span
                onClick={handleLoadMore}
                className="cursor-pointer inline-flex items-center text-blue-600 font-medium 
                 hover:text-blue-800 transition-colors duration-300"
              >
                Load More
                <svg
                  className="ml-2 w-5 h-5 text-blue-600 transition-transform duration-300"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </span>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
